import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CalendarDays, Car, ChevronLeft, ChevronRight, Compass, KeyRound, MapPin, TriangleAlert, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { moneyToIsk, fmtIsk, evalMoneyExpr } from "@/lib/price";

const API = process.env.REACT_APP_BACKEND_URL;

const pad = (n) => String(n).padStart(2, "0");
const dateKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 06:00 – 23:00
const GRID_COLS = "150px 72px repeat(18, minmax(58px, 1fr))";

const jobHour = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(t || "");
  if (!m) return null;
  const h = parseInt(m[1], 10);
  return h >= 6 && h <= 23 ? h : null;
};

const VehicleBadge = ({ job, compact = false }) => {
  if (job.driver_only)
    return (
      <span className="inline-flex items-center gap-1 border border-sky-400/40 bg-sky-400/10 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-sky-300">
        <UserRound className="h-2.5 w-2.5" /> Driver only
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-amber-200">
      <Car className="h-2.5 w-2.5" /> {job.vehicle || "Vehicle"}
      {!compact && job.headcount > 0 && <span className="text-neutral-400">· {job.headcount} pax</span>}
    </span>
  );
};

const JobBlock = ({ j, onDragStart, dropProps = {} }) => (
  <button
    type="button"
    draggable
    onDragStart={(e) => onDragStart(e, j)}
    {...dropProps}
    data-testid={`cal-block-${j.item_id}-${j.date}`}
    onClick={() => document.getElementById(`cal-job-${j.item_id}-${j.date}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
    title={`${j.time || "any time"} — ${j.title} — ${j.client} · drag onto a staff row to assign`}
    className={`mb-1 block w-full cursor-grab border-l-2 px-1.5 py-1 text-left transition-opacity hover:opacity-80 active:cursor-grabbing ${
      j.driver_only ? "border-sky-400 bg-sky-400/10" : "border-amber-400 bg-amber-400/10"
    } ${!j.assignee.trim() ? "ring-1 ring-red-500/60" : ""}`}
  >
    <span className="block text-[8px] font-semibold text-neutral-100">{j.time || "—"} {j.client}</span>
    <span className="block truncate text-[8px] leading-tight text-neutral-400">{j.title}</span>
    <span className={`mt-0.5 block text-[7px] font-bold uppercase tracking-wider ${j.driver_only ? "text-sky-300" : "text-amber-300"}`}>
      {j.driver_only ? "Driver only" : j.vehicle || "Vehicle"}
    </span>
  </button>
);

// Month calendar of client stays: one continuous bar per trip from arrival to departure,
// labelled with the client and the stay length. Drafts render faded.
const ClientStaysCalendar = ({ trips, cursor, move, monthLabel, todayKey }) => {
  const iso = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const firstWeekday = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7; // Monday-first
  const start = new Date(cursor.y, cursor.m, 1 - firstWeekday);
  const weeks = [];
  const d = new Date(start);
  while (weeks.length < 6) {
    const wk = Array.from({ length: 7 }, () => {
      const dd = new Date(d);
      d.setDate(d.getDate() + 1);
      return dd;
    });
    weeks.push(wk);
    if (d.getMonth() !== cursor.m || weeks.length === 6) break;
  }
  const stays = (trips || [])
    .filter((t) => t.arrival_date && t.departure_date)
    .map((t) => ({ ...t, nights: Math.max(1, Math.round((new Date(`${t.departure_date}T00:00:00`) - new Date(`${t.arrival_date}T00:00:00`)) / 86400000)) }));

  return (
    <div data-testid="client-stays-calendar" className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="client-cal-prev" onClick={() => move(-1)} aria-label="Previous month" className="border border-neutral-700 p-2 text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h2 data-testid="client-cal-label" className="min-w-44 text-center font-serif text-2xl font-light text-neutral-50">{monthLabel}</h2>
          <button data-testid="client-cal-next" onClick={() => move(1)} aria-label="Next month" className="border border-neutral-700 p-2 text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
          {stays.length} client{stays.length === 1 ? "" : "s"} with dates · faded = draft not yet sent
        </p>
      </div>

      <div className="mt-4 border border-neutral-800/80 bg-[#0c0c10]">
        <div className="grid grid-cols-7 border-b border-neutral-800/70">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-1.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-300/60">{w}</div>
          ))}
        </div>
        {weeks.map((wk, wi) => {
          const wkStart = iso(wk[0]);
          const wkEnd = iso(wk[6]);
          const overlapping = stays
            .filter((t) => t.arrival_date <= wkEnd && t.departure_date >= wkStart)
            .sort((a, b) => a.arrival_date.localeCompare(b.arrival_date) || b.departure_date.localeCompare(a.departure_date));
          const lanes = [];
          const bars = overlapping.map((t) => {
            const cs = t.arrival_date > wkStart ? wk.findIndex((x) => iso(x) === t.arrival_date) + 1 : 1;
            const ce = t.departure_date < wkEnd ? wk.findIndex((x) => iso(x) === t.departure_date) + 1 : 7;
            let lane = 0;
            while ((lanes[lane] || []).some((b) => !(b.ce < cs || b.cs > ce))) lane++;
            (lanes[lane] = lanes[lane] || []).push({ cs, ce });
            return { t, cs, ce, lane };
          });
          return (
            <div
              key={wi}
              className="grid grid-cols-7 border-b border-neutral-800/60 last:border-b-0"
              style={{ gridTemplateRows: `26px repeat(${Math.max(1, lanes.length)}, minmax(20px, auto))` }}
            >
              {wk.map((day, di) => (
                <div
                  key={di}
                  className={`border-r border-neutral-800/40 px-1.5 py-1 text-right text-[10px] last:border-r-0 ${
                    day.getMonth() === cursor.m ? (iso(day) === todayKey ? "font-bold text-amber-300" : "text-neutral-300") : "text-neutral-600"
                  }`}
                >
                  {day.getDate()}
                </div>
              ))}
              {bars.map(({ t, cs, ce, lane }) => (
                <div
                  key={`${t.id}-${wi}`}
                  data-testid={`client-stay-${String(t.id).slice(0, 6)}-w${wi}`}
                  title={`${t.client?.name} — arrives ${t.arrival_date}, leaves ${t.departure_date} · ${t.nights} night${t.nights === 1 ? "" : "s"}${t.pax ? ` · ${t.pax} guests` : ""}`}
                  style={{ gridColumn: `${cs} / ${ce + 1}`, gridRow: lane + 2 }}
                  className={`mx-0.5 my-0.5 truncate border-l-2 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                    t.status === "draft" ? "border-dashed border-neutral-500 bg-neutral-500/10 text-neutral-400" : "border-amber-400 bg-amber-500/15 text-amber-200"
                  }`}
                >
                  {t.client?.name} · {t.nights} n
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};


export default function OwnerCalendar() {
  const token = localStorage.getItem("er-owner-token") || "";
  const [jobs, setJobs] = useState([]);
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [endlocs, setEndlocs] = useState({});
  const [newDriver, setNewDriver] = useState("");
  const [newRole, setNewRole] = useState("driver");
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(dateKey(today.getFullYear(), today.getMonth(), today.getDate()));
  const [view, setView] = useState("day");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/owner/calendar`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setJobs(r.data.jobs || []);
        setEndlocs(r.data.endlocs || {});
      })
      .catch(() => toast.error("Could not load the allocation calendar"))
      .finally(() => setLoading(false));
    axios
      .get(`${API}/api/owner/drivers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDrivers(r.data.drivers || []))
      .catch(() => {});
    axios
      .get(`${API}/api/owner/trips`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setTrips(r.data.trips || []))
      .catch(() => {});
  }, [token]);

  const byDate = useMemo(() => {
    const map = {};
    for (const j of jobs) (map[j.date] = map[j.date] || []).push(j);
    return map;
  }, [jobs]);

  const monthLabel = new Date(cursor.y, cursor.m).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const firstWeekday = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7; // Monday-first
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());

  const monthJobs = jobs.filter((j) => j.date.startsWith(`${cursor.y}-${pad(cursor.m + 1)}`));
  const selectedJobs = byDate[selected] || [];
  const dayDrivers = [...new Set(selectedJobs.map((j) => j.assignee.trim()).filter(Boolean))];
  const unassignedDay = selectedJobs.filter((j) => !j.assignee.trim()).length;
  const selectedLabel = new Date(`${selected}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // staff rows: unassigned first, then every roster driver, plus any assignee not in the roster
  const staffRows = useMemo(() => {
    const names = drivers.map((d) => d.name);
    for (const j of selectedJobs) {
      const a = j.assignee.trim();
      if (a && !names.some((n) => n.toLowerCase() === a.toLowerCase())) names.push(a);
    }
    return names;
  }, [drivers, selectedJobs]);

  const move = (dir) => {
    const d = new Date(cursor.y, cursor.m + dir, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  const shiftDay = (n) => {
    const d = new Date(`${selected}T12:00:00`);
    d.setDate(d.getDate() + n);
    setSelected(dateKey(d.getFullYear(), d.getMonth(), d.getDate()));
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  const saveAssignee = async (job, value) => {
    if (value === job.assignee) return;
    try {
      await axios.patch(
        `${API}/api/owner/trips/${job.trip_id}/ops`,
        { kind: "item", key: job.item_id, field: "assignee", value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobs((js) => js.map((j) => (j.trip_id === job.trip_id && j.item_id === job.item_id ? { ...j, assignee: value } : j)));
      toast.success(value ? `Assigned to ${value}` : "Assignment cleared");
      // grow the roster automatically when a new name is allocated
      if (value && !drivers.some((d) => d.name.toLowerCase() === value.toLowerCase())) {
        try {
          const r = await axios.post(`${API}/api/owner/drivers`, { name: value }, { headers: { Authorization: `Bearer ${token}` } });
          setDrivers((ds) => [...ds, r.data].sort((a, b) => a.name.localeCompare(b.name)));
        } catch {}
      }
    } catch {
      toast.error("Could not save the assignment");
    }
  };

  const addDriver = async () => {
    const name = newDriver.trim();
    if (!name) return;
    try {
      const r = await axios.post(`${API}/api/owner/drivers`, { name, role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      setDrivers((ds) => (ds.some((d) => d.id === r.data.id) ? ds : [...ds, r.data].sort((a, b) => a.name.localeCompare(b.name))));
      setNewDriver("");
      toast.success(`${r.data.name} added to the roster`);
    } catch {
      toast.error("Could not add to the roster");
    }
  };

  const removeDriver = async (d) => {
    try {
      await axios.delete(`${API}/api/owner/drivers/${d.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDrivers((ds) => ds.filter((x) => x.id !== d.id));
      toast.success(`${d.name} removed from the roster`);
    } catch {
      toast.error("Could not remove the driver");
    }
  };

  const saveEndloc = async (driver, date, value) => {
    const key = `${driver}|${date}`;
    if ((endlocs[key] || "") === value) return;
    try {
      await axios.put(`${API}/api/owner/calendar/endloc`, { driver, date, location: value }, { headers: { Authorization: `Bearer ${token}` } });
      setEndlocs((m) => {
        const c = { ...m };
        if (value) c[key] = value;
        else delete c[key];
        return c;
      });
      toast.success("End-of-day location saved");
    } catch {
      toast.error("Could not save the location");
    }
  };

  // where a driver finishes vs where their next job starts
  const nextJobFor = (driver, date) =>
    jobs
      .filter((j) => j.assignee.trim().toLowerCase() === driver.toLowerCase() && j.date > date)
      .sort((a, b) => a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""))[0];

  const placesMatch = (a, b) => {
    const n = (s) => (s || "").toLowerCase().replace(/[^a-záðéíóúýþæö0-9]+/gi, "");
    const x = n(a);
    const y = n(b);
    return !!x && !!y && (x.includes(y) || y.includes(x));
  };

  const knownPlaces = [...new Set(jobs.map((j) => j.place).filter(Boolean))].sort();

  // money: owed per staff member across all their jobs, and for the selected day
  const owedBy = useMemo(() => {
    const m = {};
    for (const j of jobs) {
      const a = j.assignee.trim().toLowerCase();
      if (a) m[a] = (m[a] || 0) + moneyToIsk(j.cost);
    }
    return m;
  }, [jobs]);
  const owedFor = (name) => owedBy[name.trim().toLowerCase()] || 0;
  const owedForDay = (name) =>
    selectedJobs.filter((j) => j.assignee.trim().toLowerCase() === name.trim().toLowerCase()).reduce((s, j) => s + moneyToIsk(j.cost), 0);
  const roleOf = (name) => drivers.find((d) => d.name.toLowerCase() === name.trim().toLowerCase())?.role || "driver";

  // drag a roster person onto a job (block or detail row) to allocate them
  const onStaffDragStart = (e, name) => {
    e.dataTransfer.setData("application/x-er-staff", JSON.stringify({ name }));
    e.dataTransfer.effectAllowed = "move";
  };

  const jobDropProps = (job) => ({
    onDragOver: (e) => {
      if (e.dataTransfer.types.includes("application/x-er-staff")) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }
    },
    onDrop: (e) => {
      const raw = e.dataTransfer.getData("application/x-er-staff");
      if (!raw) return;
      e.preventDefault();
      try {
        const d = JSON.parse(raw);
        if (job.assignee.trim().toLowerCase() !== d.name.toLowerCase()) saveAssignee(job, d.name);
      } catch {}
    },
  });

  const saveCost = async (job, value) => {
    const v = evalMoneyExpr(value) ?? value; // calculator works here too
    if (v === (job.cost || "")) return;
    try {
      await axios.patch(
        `${API}/api/owner/trips/${job.trip_id}/ops`,
        { kind: "item", key: job.item_id, field: "cost", value: v },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobs((js) => js.map((j) => (j.trip_id === job.trip_id && j.item_id === job.item_id ? { ...j, cost: v } : j)));
      toast.success("Cost updated");
    } catch {
      toast.error("Could not save the cost");
    }
  };

  // drag a job block onto a staff row (or back onto Unassigned) to allocate it
  const [dropRow, setDropRow] = useState(null);

  const onJobDragStart = (e, j) => {
    e.dataTransfer.setData("application/x-er-job", JSON.stringify({ trip_id: j.trip_id, item_id: j.item_id }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onJobDrop = (e, staffName) => {
    e.preventDefault();
    setDropRow(null);
    try {
      const d = JSON.parse(e.dataTransfer.getData("application/x-er-job"));
      const job = jobs.find((j) => j.trip_id === d.trip_id && j.item_id === d.item_id);
      if (job && job.assignee.trim().toLowerCase() !== staffName.trim().toLowerCase()) saveAssignee(job, staffName);
    } catch {}
  };

  const rowDropProps = (key, staffName) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },
    onDragEnter: () => setDropRow(key),
    onDragLeave: () => setDropRow(null),
    onDrop: (e) => onJobDrop(e, staffName),
  });

  if (!token) {
    return (
      <div data-testid="calendar-locked" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        <p className="mt-8 font-serif text-3xl font-light text-neutral-200">The private door is closed.</p>
        <p className="mt-3 max-w-sm text-sm text-neutral-500">Sign in at the concierge desk first — your key opens the allocation calendar too.</p>
        <Link
          to="/owner"
          data-testid="calendar-goto-login"
          className="mt-8 flex items-center gap-2 bg-amber-500 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
        >
          <KeyRound className="h-4 w-4" /> Go to Sign-In
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="calendar-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="" className="h-10 w-auto mix-blend-screen" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Staff Allocation</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">Driving Calendar</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="cal-view-day"
              onClick={() => setView("day")}
              aria-pressed={view === "day"}
              className={`border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                view === "day" ? "border-amber-400 bg-amber-500 text-black" : "border-neutral-700 text-neutral-300 hover:border-amber-400 hover:text-amber-200"
              }`}
            >
              Day
            </button>
            <button
              data-testid="cal-view-month"
              onClick={() => setView("month")}
              aria-pressed={view === "month"}
              className={`flex items-center gap-1.5 border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                view === "month" ? "border-amber-400 bg-amber-500 text-black" : "border-neutral-700 text-neutral-300 hover:border-amber-400 hover:text-amber-200"
              }`}
            >
              <CalendarDays className="h-3.5 w-3.5" /> Month
            </button>
            <button
              data-testid="cal-view-clients"
              onClick={() => setView("clients")}
              aria-pressed={view === "clients"}
              className={`border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                view === "clients" ? "border-amber-400 bg-amber-500 text-black" : "border-neutral-700 text-neutral-300 hover:border-amber-400 hover:text-amber-200"
              }`}
            >
              Clients
            </button>
            <Link
              to="/owner/trips"
              data-testid="calendar-back-to-trips"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Trips
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        {/* legend */}
        {view !== "clients" && (
        <div data-testid="calendar-legend" className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.14em] text-neutral-400">
          <span className="flex items-center gap-1.5"><Car className="h-3.5 w-3.5 text-amber-300" /> Vehicle needed — V-Class ≤ 6 pax · Sprinter 7–16</span>
          <span className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-sky-300" /> Driver only — client has the vehicle</span>
          <span className="flex items-center gap-1.5"><TriangleAlert className="h-3.5 w-3.5 text-red-400" /> No staff allocated yet</span>
        </div>
        )}

        {/* staff roster — drivers & guides, draggable onto jobs */}
        {view !== "clients" && (
        <div data-testid="roster-panel" className="mt-4 border border-neutral-800/80 bg-[#0c0c10] px-4 py-3">
          <p className="text-[9px] font-semibold uppercase tracking-[0.28em] text-amber-300/80">Staff roster — drag a name onto a job to allocate</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {drivers.map((d) => (
              <span
                key={d.id}
                data-testid={`roster-driver-${d.id}`}
                draggable
                onDragStart={(e) => onStaffDragStart(e, d.name)}
                title={`${d.role === "guide" ? "Guide" : "Driver"} — drag onto a job to allocate`}
                className="flex cursor-grab items-center gap-1.5 border border-amber-500/30 bg-amber-500/[0.06] px-2.5 py-1 text-[11px] text-amber-100 active:cursor-grabbing"
              >
                {d.role === "guide" ? <Compass className="h-3 w-3 text-emerald-300/80" /> : <Car className="h-3 w-3 text-sky-300/80" />}
                {d.name}
                {owedFor(d.name) > 0 && <span className="text-[9px] uppercase tracking-wide text-amber-300/80">· owe {fmtIsk(owedFor(d.name))}</span>}
                <button data-testid={`roster-remove-${d.id}`} onClick={() => removeDriver(d)} aria-label={`Remove ${d.name}`} className="text-neutral-500 transition-colors hover:text-red-400">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {drivers.length === 0 && <span className="text-[10px] text-neutral-600">No staff saved yet — allocate a job and the name saves itself, or add one here.</span>}
            <input
              data-testid="roster-add-input"
              value={newDriver}
              onChange={(e) => setNewDriver(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addDriver();
              }}
              placeholder="Add a driver or guide…"
              className="w-40 border border-neutral-700 bg-[#0d0d12] px-2.5 py-1 text-[11px] text-neutral-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
            />
            <select
              data-testid="roster-role-select"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="border border-neutral-700 bg-[#0d0d12] px-2 py-1 text-[11px] text-neutral-100 outline-none focus:border-amber-400"
            >
              <option value="driver">Driver</option>
              <option value="guide">Guide</option>
            </select>
            <button
              data-testid="roster-add-button"
              onClick={addDriver}
              className="border border-amber-500/50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
            >
              Add
            </button>
          </div>
        </div>
        )}

        {view === "day" ? (
          <>
            {/* day bar */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button data-testid="cal-day-prev" onClick={() => shiftDay(-1)} aria-label="Previous day" className="border border-neutral-700 p-2 text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h2 data-testid="cal-day-label" className="min-w-44 text-center font-serif text-2xl font-light text-neutral-50">{selectedLabel}</h2>
                <button data-testid="cal-day-next" onClick={() => shiftDay(1)} aria-label="Next day" className="border border-neutral-700 p-2 text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  data-testid="cal-today"
                  onClick={() => setSelected(todayKey)}
                  className="ml-2 border border-amber-500/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                >
                  Today
                </button>
              </div>
              <p data-testid="cal-day-stats" className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                {selectedJobs.length} driving job{selectedJobs.length === 1 ? "" : "s"}
                {unassignedDay > 0 && <span className="ml-2 text-red-400">· {unassignedDay} need staff</span>}
              </p>
            </div>

            {/* hour-by-hour timeline — staff down the left, hours across */}
            <div data-testid="cal-timeline" className="mt-4 overflow-x-auto border border-neutral-800/80">
              <div className="min-w-[1120px]">
                <div className="grid bg-[#0c0c10]" style={{ gridTemplateColumns: GRID_COLS }}>
                  <div className="px-3 py-2 text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-300/70">Staff</div>
                  <div className="border-l border-neutral-800/70 px-2 py-2 text-center text-[9px] font-semibold uppercase tracking-[0.14em] text-neutral-500">Any</div>
                  {HOURS.map((h) => (
                    <div key={h} className="border-l border-neutral-800/70 px-1 py-2 text-center text-[9px] font-semibold tracking-wider text-amber-300/60">
                      {pad(h)}
                    </div>
                  ))}
                </div>

                {/* unassigned row — drop a block here to unassign */}
                <div
                  className={`grid border-t border-neutral-800/70 transition-colors ${dropRow === "unassigned" ? "bg-amber-500/10" : "bg-red-500/[0.03]"}`}
                  style={{ gridTemplateColumns: GRID_COLS }}
                  data-testid="cal-staff-row-unassigned"
                  {...rowDropProps("unassigned", "")}
                >
                  <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-red-400">Unassigned</div>
                  <div className="border-l border-neutral-800/70 px-1 py-1">
                    {selectedJobs.filter((j) => !j.assignee.trim() && jobHour(j.time) === null).map((j) => (
                      <JobBlock key={`${j.item_id}-any`} j={j} onDragStart={onJobDragStart} dropProps={jobDropProps(j)} />
                    ))}
                  </div>
                  {HOURS.map((h) => (
                    <div key={h} className="border-l border-neutral-800/50 px-1 py-1">
                      {selectedJobs.filter((j) => !j.assignee.trim() && jobHour(j.time) === h).map((j) => (
                        <JobBlock key={j.item_id} j={j} onDragStart={onJobDragStart} dropProps={jobDropProps(j)} />
                      ))}
                    </div>
                  ))}
                </div>

                {/* one row per staff member — drop a block to assign */}
                {staffRows.map((name) => (
                  <div
                    key={name}
                    className={`grid border-t border-neutral-800/70 transition-colors ${dropRow === name ? "bg-amber-500/10" : ""}`}
                    style={{ gridTemplateColumns: GRID_COLS }}
                    data-testid={`cal-staff-row-${name}`}
                    {...rowDropProps(name, name)}
                  >
                    <div className="px-3 py-2">
                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-neutral-100">
                        {roleOf(name) === "guide" ? <Compass className="h-3 w-3 text-emerald-300/80" /> : <Car className="h-3 w-3 text-sky-300/80" />}
                        {name}
                      </span>
                      {owedForDay(name) > 0 && <span className="mt-0.5 block text-[8px] uppercase tracking-wide text-amber-300/80">owed {fmtIsk(owedForDay(name))} today</span>}
                    </div>
                    <div className="border-l border-neutral-800/70 px-1 py-1">
                      {selectedJobs.filter((j) => j.assignee.trim().toLowerCase() === name.toLowerCase() && jobHour(j.time) === null).map((j) => (
                        <JobBlock key={`${j.item_id}-any`} j={j} onDragStart={onJobDragStart} dropProps={jobDropProps(j)} />
                      ))}
                    </div>
                    {HOURS.map((h) => (
                      <div key={h} className="border-l border-neutral-800/50 px-1 py-1">
                        {selectedJobs.filter((j) => j.assignee.trim().toLowerCase() === name.toLowerCase() && jobHour(j.time) === h).map((j) => (
                          <JobBlock key={j.item_id} j={j} onDragStart={onJobDragStart} dropProps={jobDropProps(j)} />
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
                {staffRows.length === 0 && selectedJobs.length === 0 && (
                  <p className="border-t border-neutral-800/70 px-4 py-6 text-center text-xs text-neutral-500">
                    {loading ? "Loading…" : "No driving jobs this day."}
                  </p>
                )}
              </div>
            </div>
          </>
        ) : view === "month" ? (
          <>
            {/* month bar */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button data-testid="cal-prev" onClick={() => move(-1)} aria-label="Previous month" className="border border-neutral-700 p-2 text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h2 data-testid="cal-month-label" className="min-w-44 text-center font-serif text-2xl font-light text-neutral-50">{monthLabel}</h2>
                <button data-testid="cal-next" onClick={() => move(1)} aria-label="Next month" className="border border-neutral-700 p-2 text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  data-testid="cal-today-month"
                  onClick={() => {
                    setCursor({ y: today.getFullYear(), m: today.getMonth() });
                    setSelected(todayKey);
                  }}
                  className="ml-2 border border-amber-500/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                >
                  Today
                </button>
              </div>
              <p data-testid="cal-month-stats" className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                {monthJobs.length} driving job{monthJobs.length === 1 ? "" : "s"} this month
                {monthJobs.filter((j) => !j.assignee.trim()).length > 0 && <span className="ml-2 text-red-400">· {monthJobs.filter((j) => !j.assignee.trim()).length} need staff</span>}
              </p>
            </div>

            {/* month grid — tap a day to open its day view */}
            <div className="mt-4 grid grid-cols-7 gap-px border border-neutral-800/80 bg-neutral-800/80">
              {WEEKDAYS.map((w) => (
                <div key={w} className="bg-[#0c0c10] px-2 py-2 text-center text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-300/70">
                  {w}
                </div>
              ))}
              {cells.map((d, idx) => {
                if (d === null) return <div key={`b${idx}`} className="min-h-16 bg-[#08080a] sm:min-h-24" />;
                const key = dateKey(cursor.y, cursor.m, d);
                const dayJobs = byDate[key] || [];
                const isSel = key === selected;
                return (
                  <button
                    key={key}
                    data-testid={`cal-day-${key}`}
                    onClick={() => {
                      setSelected(key);
                      setView("day");
                    }}
                    className={`min-h-16 bg-[#0a0a0e] p-1.5 text-left align-top transition-colors sm:min-h-24 ${
                      isSel ? "bg-amber-500/[0.08] outline outline-1 outline-amber-400/50" : "hover:bg-white/[0.03]"
                    }`}
                  >
                    <span className={`inline-flex h-5 w-5 items-center justify-center text-[10px] ${key === todayKey ? "bg-amber-500 font-bold text-black" : "text-neutral-400"}`}>
                      {d}
                    </span>
                    <span className="mt-1 flex flex-col gap-1">
                      {dayJobs.slice(0, 3).map((j) => (
                        <span
                          key={`${j.item_id}-${j.date}`}
                          className={`block truncate border-l-2 px-1 py-0.5 text-[8px] leading-tight ${
                            j.driver_only ? "border-sky-400 text-sky-200" : "border-amber-400 text-amber-100"
                          } ${!j.assignee.trim() ? "bg-red-500/10" : "bg-white/[0.03]"}`}
                        >
                          {j.time || "—"} {j.client}
                        </span>
                      ))}
                      {dayJobs.length > 3 && <span className="px-1 text-[8px] text-neutral-500">+{dayJobs.length - 3} more</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <ClientStaysCalendar trips={trips} cursor={cursor} move={move} monthLabel={monthLabel} todayKey={todayKey} />
        )}

        {/* selected day detail */}
        {view !== "clients" && (
        <div data-testid="cal-detail-panel" className="mt-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-amber-300/80">
            {new Date(`${selected}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} — allocate staff
          </p>
          {selectedJobs.length === 0 ? (
            <p className="mt-3 border border-neutral-800/80 bg-[#0c0c10] px-4 py-6 text-center text-xs text-neutral-500">
              {loading ? "Loading…" : "No driving jobs this day."}
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              {selectedJobs.map((j) => (
                <div
                  key={`${j.item_id}-${j.date}`}
                  id={`cal-job-${j.item_id}-${j.date}`}
                  data-testid={`cal-job-${j.item_id}-${j.date}`}
                  {...jobDropProps(j)}
                  className={`border-l-2 bg-[#0c0c10] px-4 py-3 ${j.driver_only ? "border-sky-400/60" : "border-amber-400/60"}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-serif text-base leading-snug text-neutral-50">
                      <span className="mr-2 text-xs tracking-[0.1em] text-amber-200/80">{j.time || "—"}</span>
                      {j.title}
                    </p>
                    <VehicleBadge job={j} />
                  </div>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                    {j.client} · trip day {j.day}
                    {j.headcount > 0 && ` · ${j.headcount} pax`}
                    {j.trip_status !== "confirmed" && <span className="ml-2 text-neutral-600">({j.trip_status.replace(/_/g, " ")})</span>}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] uppercase tracking-[0.14em] text-neutral-400">
                    <span>
                      Charging <span data-testid={`cal-charge-${j.item_id}`} className="text-amber-200">{j.price ? fmtIsk(moneyToIsk(j.price)) : "—"}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      Pay {j.assignee.trim() ? j.assignee.trim().split(" ")[0] : "them"}
                      <input
                        key={`cost-${j.item_id}-${j.date}:${j.cost || ""}`}
                        data-testid={`cal-cost-${j.item_id}`}
                        defaultValue={j.cost || ""}
                        placeholder="0"
                        title="What you pay the driver or guide for this job"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                        onBlur={(e) => saveCost(j, e.target.value.trim())}
                        className="w-24 border border-neutral-700 bg-[#0d0d12] px-2 py-1 text-[11px] normal-case tracking-normal text-neutral-100 outline-none focus:border-amber-400"
                      />
                    </span>
                    <span>
                      Profit{" "}
                      <span data-testid={`cal-profit-${j.item_id}`} className={moneyToIsk(j.price) - moneyToIsk(j.cost) >= 0 ? "text-emerald-300" : "text-red-400"}>
                        {fmtIsk(moneyToIsk(j.price) - moneyToIsk(j.cost))}
                      </span>
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <input
                      key={`${j.item_id}:${j.assignee}`}
                      data-testid={`cal-assign-${j.item_id}`}
                      defaultValue={j.assignee}
                      list="driver-roster"
                      placeholder="Allocate driver / guide…"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                      }}
                      onBlur={(e) => saveAssignee(j, e.target.value.trim())}
                      className={`w-52 border px-2.5 py-1.5 text-xs outline-none transition-colors ${
                        j.assignee.trim()
                          ? "border-neutral-700 bg-[#0d0d12] text-neutral-100 focus:border-amber-400"
                          : "border-red-900/60 bg-red-500/[0.05] text-red-100 placeholder:text-red-400/60 focus:border-red-400"
                      }`}
                    />
                    {!j.assignee.trim() && (
                      <span data-testid={`cal-needs-staff-${j.item_id}`} className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-red-400">
                        <TriangleAlert className="h-3 w-3" /> Needs {j.driver_only ? "a driver" : "staff + vehicle"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {dayDrivers.length > 0 && (
            <div data-testid="endloc-section" className="mt-6 border border-neutral-800/80 bg-[#0c0c10] px-4 py-3">
              <p className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.28em] text-amber-300/80">
                <MapPin className="h-3 w-3" /> End of day — where each driver finishes
              </p>
              <div className="mt-3 space-y-3">
                {dayDrivers.map((d) => {
                  const key = `${d}|${selected}`;
                  const loc = endlocs[key] || "";
                  const next = nextJobFor(d, selected);
                  const cmp = loc && next && next.place ? placesMatch(loc, next.place) : null;
                  return (
                    <div key={d} data-testid={`endloc-row-${d}`} className="flex flex-wrap items-center gap-3">
                      <span className="w-24 shrink-0 text-xs font-medium text-neutral-200">{d}</span>
                      <input
                        key={`${key}:${loc}`}
                        data-testid={`endloc-input-${d}`}
                        defaultValue={loc}
                        list="known-places"
                        placeholder="Ends the day in…"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                        onBlur={(e) => saveEndloc(d, selected, e.target.value.trim())}
                        className="w-44 border border-neutral-700 bg-[#0d0d12] px-2.5 py-1.5 text-xs text-neutral-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
                      />
                      {cmp === true && (
                        <span data-testid={`endloc-status-${d}`} className="text-[10px] uppercase tracking-[0.12em] text-emerald-300/90">
                          Already in {loc} for the next job
                        </span>
                      )}
                      {cmp === false && (
                        <span data-testid={`endloc-status-${d}`} className="text-[10px] uppercase tracking-[0.12em] text-red-400">
                          Ends in {loc} — needed in {next.place} on {next.date}
                          {next.time ? ` ${next.time}` : ""}
                        </span>
                      )}
                      {cmp === null && next && (
                        <span data-testid={`endloc-status-${d}`} className="text-[10px] uppercase tracking-[0.12em] text-neutral-500">
                          Next: {next.title} · {next.date}
                          {next.time ? ` ${next.time}` : ""}
                          {next.place ? ` · ${next.place}` : ""}
                        </span>
                      )}
                      {cmp === null && !next && <span className="text-[10px] uppercase tracking-[0.12em] text-neutral-600">No upcoming jobs</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        <datalist id="driver-roster">
          {drivers.map((d) => (
            <option key={d.id} value={d.name} />
          ))}
        </datalist>
        <datalist id="known-places">
          {knownPlaces.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
      </main>
    </div>
  );
}
